function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { forwardRef } from 'react';
import { useHistory } from 'react-router-dom';
import { requiredValidation } from 'validators';
import { Controller, useForm } from 'react-hook-form';
import { Button, CustomModal, MenuItem, TextField, Typography, SplitButton, useOverlayContext } from 'seagull';
import { routes } from "../../constants";
import { useEventReporter } from "../../event-reporter";
import { useRedirectToProposal } from "../../hooks/useCreateAgreement";
import { useStoreActions } from "../../store/store-context";
import { ProposalTemplateSummaryModel } from "../../models";
import { connectControllerToTextField } from "../../utils/react-hook-form.utils";
import styles from "./CreateProposalButton.module.scss";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { SelectProposalTypeModal } from "./SelectProposalTypeModal/SelectProposalTypeModal";
import { useProposalTemplates } from "./hooks/useProposalTemplates";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export const CreateProposalButton = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    text = 'Create proposal'
  } = props;
  const {
    reportButtonClick
  } = useEventReporter();
  const {
    openCreationModal
  } = useRedirectToProposal();
  const {
    api
  } = useStoreActions();
  const {
    templates,
    isLoading
  } = useProposalTemplates({
    refreshOnMount: true
  });
  const hasProposalTemplates = !!(templates !== null && templates !== void 0 && templates.length);
  const overlayContext = useOverlayContext();
  const history = useHistory();
  const {
    NEW_PROPOSAL_TYPE_PICKER
  } = useFeatureFlags();
  const openSelectProposalTypeModal = proposalType => {
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/_jsx(SelectProposalTypeModal, {
          dismissModal: dismissModal,
          proposalType: proposalType
        });
      }
    });
  };
  const openNewProposal = async () => {
    reportButtonClick('topbar-new-agreement-btn');
    if (NEW_PROPOSAL_TYPE_PICKER && hasProposalTemplates) {
      openSelectProposalTypeModal();
      return;
    }
    await openCreationModal();
  };
  const openNewProposalFromDraft = async () => {
    reportButtonClick('topbar-new-agreement-btn');
    await openCreationModal();
  };
  const onNewTemplate = async () => {
    if (NEW_PROPOSAL_TYPE_PICKER) {
      openSelectProposalTypeModal('template');
      return;
    }
    const templateId = await overlayContext.showModal({
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/_jsx(SelectTemplateModal, {
          dismissModal: dismissModal,
          proposalTemplates: templates || []
        });
      }
    });
    if (templateId) {
      const draftId = await api.proposalTemplates.createDraft(templateId);
      history.push(routes.draft(draftId));
    }
  };
  return hasProposalTemplates ? /*#__PURE__*/_jsx(SplitButton, {
    label: text,
    "data-testid": "create-proposal-split-btn",
    onClick: openNewProposal,
    popperClassName: styles.popper,
    children: NEW_PROPOSAL_TYPE_PICKER ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(MenuItem, {
        onClick: onNewTemplate,
        "data-testid": "new-template-option",
        children: "Use a template"
      }), /*#__PURE__*/_jsx(MenuItem, {
        onClick: openNewProposalFromDraft,
        "data-testid": "new-proposal-option",
        children: "Start from scratch"
      })]
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(MenuItem, {
        onClick: openNewProposalFromDraft,
        "data-testid": "new-proposal-option",
        children: "Start from scratch"
      }), /*#__PURE__*/_jsx(MenuItem, {
        onClick: onNewTemplate,
        "data-testid": "new-template-option",
        children: "Start from template"
      })]
    })
  }) : /*#__PURE__*/_jsx(Button, {
    "data-testid": "create-proposal-btn",
    onClick: openNewProposal,
    ref: ref,
    loading: NEW_PROPOSAL_TYPE_PICKER && isLoading,
    children: text
  });
});
const SelectTemplateModal = _ref3 => {
  let {
    dismissModal,
    proposalTemplates
  } = _ref3;
  const sortedProposalTemplates = [...proposalTemplates].sort(ProposalTemplateSummaryModel.sortByTitle);
  const {
    control,
    setValue,
    handleSubmit
  } = useForm({
    mode: 'all',
    defaultValues: {
      proposalTemplateId: ''
    }
  });
  function onSubmit(_ref4) {
    let {
      proposalTemplateId
    } = _ref4;
    dismissModal(proposalTemplateId);
  }
  return /*#__PURE__*/_jsx("form", {
    onSubmit: handleSubmit(onSubmit),
    children: /*#__PURE__*/_jsx(CustomModal, {
      header: /*#__PURE__*/_jsx("div", {
        className: styles.header,
        children: /*#__PURE__*/_jsx(Typography, {
          variant: "h6",
          weight: "bold",
          children: "Choose template"
        })
      }),
      footer: /*#__PURE__*/_jsxs("div", {
        className: styles.footer,
        children: [/*#__PURE__*/_jsx(Button, {
          skin: "primaryOutline",
          "data-testid": "cancel-button",
          onClick: () => dismissModal(),
          children: "Cancel"
        }), /*#__PURE__*/_jsx(Button, {
          "data-testid": "approve-button",
          type: "submit",
          children: "Create proposal"
        })]
      }),
      minWidth: 580,
      onClose: () => dismissModal(),
      children: /*#__PURE__*/_jsx("div", {
        className: styles.body,
        children: /*#__PURE__*/_jsx(Controller, {
          control: control,
          name: "proposalTemplateId",
          rules: requiredValidation('Please select a template'),
          render: renderProps => /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({}, connectControllerToTextField(renderProps)), {}, {
            select: true,
            placeholder: "Choose a template to start from",
            label: "Template name",
            "data-testid": "template-select",
            children: sortedProposalTemplates.map(t => /*#__PURE__*/_jsx(MenuItem, {
              value: t.id,
              "data-testid": "option",
              onClick: () => setValue('proposalTemplateId', t.id),
              children: t.title
            }, t.id))
          }))
        })
      })
    })
  });
};